import bilAPI from "./api";

export const getTeacherTimetable = ({ params: { teacher } = {}, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/org/timetable-entries/`, { params: { teacher }, signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// Add a recurring lesson. `teacher` omitted = the requester's own grid;
// a manager passes it to edit a teacher in their scope.
export const createTimetableEntry = ({ body, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(`/org/timetable-entries/`, body, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const updateTimetableEntry = ({ uuid, body, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .patch(`/org/timetable-entries/${uuid}/`, body, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const deleteTimetableEntry = ({ uuid, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .delete(`/org/timetable-entries/${uuid}/`, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// Bulk import. `commit=false` returns a per-row preview without writing;
// `commit=true` writes (idempotent upsert) only when no row errored.
export const importTimetable = ({ rows, commit = false, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(`/org/timetable-entries/import/`, { rows, commit }, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// The LIVE committed grid on any axis — by class, by teacher, by room, or the
// whole school. Pass at most one of `group`/`teacher`/`room`; pass none for the
// whole school. `organization` defaults to the caller's own and must be inside
// their read scope (a head office may read a child school's grid).
//
// Distinct from `getTeacherTimetable` above, which is the narrow per-teacher
// list and REQUIRES `teacher`. That requirement is why, for a long time, the
// live timetable could only be looked at one teacher at a time.
//
// Resolves { scope, weekdays, periods, entries: [...] }. `weekdays`/`periods`
// come back with it because an empty Friday is still a column and the client
// cannot derive the week's shape from the rows.
export const getTimetableGrid = ({
  params: { organization, group, teacher, room } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/org/timetable-entries/grid/`, {
          // The backend calls the class axis `class`, which is a reserved word
          // in JS — hence `group` on this side.
          params: { organization, class: group, teacher, room },
          signal,
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// The live grid as an .xlsx download. Same axis params as `getTimetableGrid`.
//
// Fetched as a blob rather than linked to directly: the API is JWT-authorised
// through the axios interceptor, and a plain <a href> carries no Authorization
// header — it would 401 or, worse, download an error page named .xlsx.
//
// Resolves { blob, filename }; the filename comes from Content-Disposition so
// the server stays the authority on what the file is called.
export const exportTimetableGrid = ({
  params: { organization, group, teacher, room } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/org/timetable-entries/export/`, {
          params: { organization, class: group, teacher, room },
          responseType: "blob",
          signal,
        })
        .then((res) => {
          const disposition = res.headers?.["content-disposition"] || "";
          const match = /filename="?([^"]+)"?/.exec(disposition);
          resolve({ blob: res.data, filename: match?.[1] || "timetable.xlsx" });
        })
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};
